import { setupAGGrid } from "@/lib/AGGrid/setupAGGrid";
import { ReportReceiptsTable } from "@/app/Reports/Reports/Components/ReportReceipts/ReportReceiptsTable";
import { ReportReceiptItem } from "@/app/Reports/Reports/Components/ReportReceipts/GraphQL/reportReceiptsQuery";
import { Suspense, useCallback, useRef } from "react";
import { Button } from "@/components/catalyst/button";
import { AgGridReact } from "ag-grid-react";
import { ProcessCellForExportParams } from "ag-grid-community";
import { changeCurrencyToDollars } from "@/lib/Formatters/formatCurrency";
import { Currency } from "@/gql/graphql";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { QuarterSelector } from "@/app/Reports/Reports/Components/QuarterSelector";

setupAGGrid();

function getQuarterDateRange(
  quarter: number,
  year: number,
): { start: string; end: string } {
  // Australian FY: Q1=Jul-Sep, Q2=Oct-Dec, Q3=Jan-Mar, Q4=Apr-Jun
  const quarterMonths: Record<number, [number, number]> = {
    1: [7, year - 1],
    2: [10, year - 1],
    3: [1, year],
    4: [4, year],
  };

  const [startMonth, calendarYear] = quarterMonths[quarter];
  const startDate = new Date(calendarYear, startMonth - 1, 1);
  const endDate = new Date(calendarYear, startMonth + 2, 0);

  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  return {
    start: formatDate(startDate),
    end: formatDate(endDate),
  };
}

function getQuarterDateRangeDisplay(quarter: number, year: number): string {
  const quarterMonths: Record<number, [number, number]> = {
    1: [7, year - 1],
    2: [10, year - 1],
    3: [1, year],
    4: [4, year],
  };

  const [startMonth, calendarYear] = quarterMonths[quarter];
  const start = new Date(calendarYear, startMonth - 1, 1);
  const end = new Date(calendarYear, startMonth + 2, 0);

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return `${formatDate(start)} – ${formatDate(end)}`;
}

export function ReportReceiptsScreen() {
  const gridRef = useRef<AgGridReact<ReportReceiptItem>>(null);

  const { quarter, year } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/receipts",
  });

  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/receipts",
  });

  const dateRange = getQuarterDateRange(quarter, year);

  function handleQuarterChange(newQuarter: number, newYear: number) {
    navigate({
      params: (prev) => prev,
      search: { quarter: newQuarter, year: newYear },
    });
  }

  const onBtExport = useCallback(
    (type: "csv" | "xls") => () => {
      const onlySelected = gridRef.current?.api.getSelectedRows().length !== 0;

      // Money columns hold cents; exports carry the dollar value so the
      // numbers stay usable in a spreadsheet.
      const processCellCallback = (
        params: ProcessCellForExportParams<ReportReceiptItem>,
      ) => {
        const isCurrency = params.column.getColDef().context?.isCurrency;

        if (isCurrency && params.value != null) {
          return changeCurrencyToDollars(
            params.value,
            (params.node?.data?.currency as Currency) ?? Currency.Aud,
          );
        }

        return params.value;
      };

      if (type === "csv") {
        gridRef.current?.api.exportDataAsCsv({
          onlySelected,
          processCellCallback,
        });
      } else {
        gridRef.current?.api.exportDataAsExcel({
          onlySelected,
          processCellCallback,
        });
      }
    },
    [],
  );

  return (
    <div className="flex h-full flex-col space-y-4 p-8">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/reports"
              params={(prev) => prev}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>BAS Receipts</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">
            BAS Receipt Report
          </h2>
          <p className="text-sm text-gray-500">
            {getQuarterDateRangeDisplay(quarter, year)}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <QuarterSelector
            quarter={quarter}
            year={year}
            onQuarterChange={handleQuarterChange}
          />
          <Button outline onClick={onBtExport("csv")}>
            Export CSV
          </Button>
          <Button onClick={onBtExport("xls")}>Export Excel</Button>
        </div>
      </div>

      <div className="rounded-md bg-blue-50 p-4">
        <div className="text-sm text-blue-700">
          <strong>Note:</strong> This report shows all AUD receipts for BAS
          reporting purposes. GST amounts are displayed where applicable.
        </div>
      </div>

      <div className="grow rounded-lg">
        <Suspense fallback={<Skeleton className="mt-3 h-80" />}>
          <ReportReceiptsTable
            ref={gridRef}
            startDate={dateRange.start}
            endDate={dateRange.end}
          />
        </Suspense>
      </div>
    </div>
  );
}
